import { useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getVehicle, updateVehicle, deleteVehicle } from '../api';
import { useToast } from '../context/ToastContext';
import type { Vehicle } from '../types';
import VehicleModal from '../components/VehicleModal';

type VehicleTab = 'overview' | 'history' | 'specs' | 'public' | 'stamdata';
const VALID_TABS: VehicleTab[] = ['overview', 'history', 'specs', 'public', 'stamdata'];

// Erstatter den gamle VehicleModal til at vise en eksisterende bil. Genbruger
// VehicleModal-komponenten i 'page' mode saa al logik + alle tabs er ens med modalen.
// Tab-state laeses fra URL'en (/vehicles/:id/:tab) saa browser-back/forward virker.
export default function VehiclePage() {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();
  const params = useParams<{ id: string; tab?: string }>();
  const vehicleId = parseInt(params.id ?? '', 10);
  const rawTab = (params.tab as VehicleTab | undefined) ?? 'overview';
  const tab = VALID_TABS.includes(rawTab) ? rawTab : 'overview';

  // ?type=maintenance&id=44 peger på én bestemt begivenhed. Sendes fx fra en værksted-udgift,
  // hvor udgiften ejes af begivenheden og kun kan redigeres dér. EventHistory kunne allerede
  // tage imod det, men ingen sendte det — derfor åbnede linket aldrig noget.
  const [searchParams] = useSearchParams();
  const openType = searchParams.get('type');
  const openEventType = (openType === 'inspection' || openType === 'maintenance' || openType === 'checkup')
    ? openType : undefined;
  const openIdRaw = parseInt(searchParams.get('id') ?? '', 10);
  const openEventId = Number.isFinite(openIdRaw) ? openIdRaw : undefined;

  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(vehicleId)) { setNotFound(true); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    getVehicle(vehicleId).then(v => {
      if (cancelled) return;
      setVehicle(v);
      setLoading(false);
    }).catch(() => { if (!cancelled) { setNotFound(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [vehicleId]);

  const handleSave = async (updated: Vehicle) => {
    try {
      await updateVehicle(vehicleId, updated);
      setVehicle(updated);
      showSuccess(t('modal.saved'));
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Kunne ikke gemme');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Slet denne bil?')) return;
    try {
      await deleteVehicle(vehicleId);
      navigate('/vehicles');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Kunne ikke slette');
    }
  };

  if (notFound) return <Navigate to="/vehicles" replace />;
  if (loading || !vehicle) {
    return <main className="max-w-5xl mx-auto px-6 py-8"><p className="text-gray-400">...</p></main>;
  }

  return (
    <main className="max-w-5xl mx-auto px-6 py-8">
      <VehicleModal
        mode="page"
        vehicle={vehicle}
        onSave={handleSave}
        onSynced={setVehicle}
        onClose={() => navigate('/vehicles')}
        onDelete={handleDelete}
        initialTab={tab}
        openEventType={openEventType}
        openEventId={openEventId}
        onTabChange={next => navigate(`/vehicles/${vehicleId}/${next}`, { replace: true })}
      />
    </main>
  );
}
